import { Link, Outlet } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { LanguageSwitcher } from "@/components/language-switcher";
import { ThemeToggle } from "@/components/theme-toggle";
import { Button } from "@/components/ui/button";
import { useDict } from "@/lib/i18n/context";
import { CandidateLayout } from "@/layouts/CandidateLayout";
import type { User } from "@/lib/types";

function PublicCareersChrome({ children }: { children: React.ReactNode }) {
  const t = useDict();
  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="sticky top-0 z-40 border-b border-border bg-card/80 backdrop-blur">
        <div className="mx-auto flex max-w-5xl min-w-0 items-center justify-between gap-3 px-3 py-3 sm:gap-4 sm:px-4">
          <Link to="/" className="flex min-w-0 items-center gap-2">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-600 text-white">
              <Sparkles className="h-4 w-4" />
            </span>
            <span className="truncate font-bold tracking-wide">
              {t.common.appName}
            </span>
          </Link>
          <div className="flex shrink-0 items-center gap-2.5 sm:gap-3">
            <ThemeToggle />
            <LanguageSwitcher />
            <Link
              to="/careers"
              className="hidden text-sm font-medium text-brand-700 dark:text-brand-300 md:inline"
            >
              {t.nav.careers}
            </Link>
            <Link
              to="/contact"
              className="hidden text-sm font-medium text-muted hover:text-foreground md:inline"
            >
              {t.nav.contact}
            </Link>
            <Link
              to="/sign-in"
              className="text-sm font-medium text-muted hover:text-foreground md:hidden"
            >
              {t.common.signIn}
            </Link>
            <Link to="/sign-in" className="hidden md:inline-flex">
              <Button variant="ghost" size="sm">
                {t.common.signIn}
              </Button>
            </Link>
            <Link to="/sign-up?as=candidate">
              <Button size="sm" className="px-3">
                {t.common.signUp}
              </Button>
            </Link>
          </div>
        </div>
      </header>
      <main className="mx-auto w-full max-w-5xl flex-1 p-4 md:p-8">
        {children}
      </main>
    </div>
  );
}

/** Careers browse: candidate chrome when logged in as candidate; public chrome otherwise. */
export function CareersShell({
  user,
  onSignOut,
}: {
  user: User | null | undefined;
  onSignOut: () => void;
}) {
  if (user === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center text-muted">
        Loading…
      </div>
    );
  }
  if (user?.role === "candidate") {
    return (
      <CandidateLayout user={user} onSignOut={onSignOut}>
        <Outlet />
      </CandidateLayout>
    );
  }
  return (
    <PublicCareersChrome>
      <Outlet />
    </PublicCareersChrome>
  );
}
